/* eslint-disable no-console */
import NotificationSubscriptions from '../api/notificationSubscription';
import auth from '../api/auth';

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator)) {
    // Service Worker isn't supported on this browser, disable or hide UI.
    return;
  }

  if (!('PushManager' in window)) {
    // Push isn't supported on this browser, disable or hide UI.
    return;
  }

  navigator.serviceWorker
    .register('/sw.js')
    .then(registration => callback(registration))
    .catch(registrationError => {
      // eslint-disable-next-line
      console.log('SW registration failed: ', registrationError);
    });
};

export const hasPushPermissions = () => {
  if ('Notification' in window) {
    return Notification.permission === 'granted';
  }
  return false;
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => ({
  subscription_type: 'browser_push',
  subscription_attributes: {
    endpoint: subscription.endpoint,
    p256dh: generateKeys(subscription.getKey('p256dh')),
    auth: generateKeys(subscription.getKey('auth')),
  },
});

export const sendRegistrationToServer = subscription => {
  if (auth.hasAuthCookie()) {
    return NotificationSubscriptions.create(
      getPushSubscriptionPayload(subscription)
    );
  }
  return null;
};

// Motivos de falha ao ativar as notificações do navegador. Quem chama decide
// como avisar o usuário — o helper nunca falha em silêncio.
export const PUSH_PERMISSION_ERRORS = {
  UNSUPPORTED: 'unsupported',
  BLOCKED: 'blocked',
  DISMISSED: 'dismissed',
  MISSING_VAPID_KEY: 'missing_vapid_key',
  SERVICE_WORKER_FAILED: 'service_worker_failed',
  SUBSCRIPTION_FAILED: 'subscription_failed',
};

const SERVICE_WORKER_TIMEOUT_MS = 10000;

const withTimeout = (promise, timeoutMs, message) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), timeoutMs);
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      error => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

// `navigator.serviceWorker.ready` não resolve NEM rejeita quando não existe
// service worker ativo no escopo — esperar só por ela faz o clique morrer em
// silêncio. Registramos explicitamente (a chamada é idempotente) e limitamos a
// espera, para que qualquer falha vire um erro visível.
export const getServiceWorkerRegistration = () => {
  if (!('serviceWorker' in navigator)) {
    return Promise.reject(new Error('Service worker is not supported'));
  }

  return withTimeout(
    navigator.serviceWorker
      .register('/sw.js')
      .then(() => navigator.serviceWorker.ready),
    SERVICE_WORKER_TIMEOUT_MS,
    'Service worker did not become ready in time'
  );
};

export const registerSubscription = (
  onSuccess = () => {},
  onError = () => {}
) => {
  const vapidPublicKey = window.chatwootConfig?.vapidPublicKey;
  if (!vapidPublicKey || !vapidPublicKey.length) {
    console.error('Push subscription aborted: VAPID public key is missing');
    onError(PUSH_PERMISSION_ERRORS.MISSING_VAPID_KEY);
    return;
  }

  // Evita avisar o usuário duas vezes quando a falha já foi identificada.
  let errorReported = false;

  const subscribeOptions = {
    userVisibleOnly: true,
    applicationServerKey: vapidPublicKey,
  };

  // Uma inscrição antiga criada com outra chave VAPID faz o `subscribe` falhar
  // com InvalidStateError. O navegador só aceita a nova depois que a obsoleta
  // é removida, então removemos e tentamos uma única vez mais.
  const subscribeReplacingStale = registration =>
    registration.pushManager.subscribe(subscribeOptions).catch(error => {
      console.warn('Replacing a stale push subscription:', error);
      return registration.pushManager
        .getSubscription()
        .then(stale => (stale ? stale.unsubscribe() : null))
        .then(() => registration.pushManager.subscribe(subscribeOptions));
    });

  getServiceWorkerRegistration()
    .catch(error => {
      console.error('Service worker unavailable for push:', error);
      errorReported = true;
      onError(PUSH_PERMISSION_ERRORS.SERVICE_WORKER_FAILED);
      throw error;
    })
    .then(subscribeReplacingStale)
    .then(subscription => {
      const request = sendRegistrationToServer(subscription);
      // Sem cookie de sessão nada chega ao servidor. Relatar sucesso aqui
      // deixaria a chave acesa sem nenhuma inscrição registrada.
      if (!request) {
        throw new Error('Auth cookie is missing; subscription was not saved');
      }
      return request;
    })
    .then(() => {
      onSuccess();
    })
    .catch(error => {
      if (errorReported) return;
      console.error('Push subscription registration failed:', error);
      onError(PUSH_PERMISSION_ERRORS.SUBSCRIPTION_FAILED);
    });
};

export const requestPushPermissions = ({
  onSuccess = () => {},
  onError = () => {},
  onPrompt = () => {},
}) => {
  if (!('Notification' in window)) {
    // eslint-disable-next-line no-console
    console.warn('Notification is not supported');
    onError(PUSH_PERMISSION_ERRORS.UNSUPPORTED);
    return;
  }

  if (Notification.permission === 'granted') {
    registerSubscription(onSuccess, onError);
    return;
  }

  // O navegador já bloqueou o site: `requestPermission` não abre nada e o
  // clique morreria sem resposta. Só o usuário pode desbloquear nas
  // configurações do navegador.
  if (Notification.permission === 'denied') {
    onError(PUSH_PERMISSION_ERRORS.BLOCKED);
    return;
  }

  // No modo "quieter messaging" o Chrome não abre janela nenhuma: o pedido vira
  // um ícone discreto na barra de endereço e este callback só roda quando o
  // usuário interage com ele. Sem avisar aqui, o clique parece não fazer nada.
  onPrompt();

  Notification.requestPermission(permission => {
    if (permission === 'granted') {
      registerSubscription(onSuccess, onError);
      return;
    }

    onError(
      permission === 'denied'
        ? PUSH_PERMISSION_ERRORS.BLOCKED
        : PUSH_PERMISSION_ERRORS.DISMISSED
    );
  });
};
